import { useState } from 'react';
import { Button, InputTextArea, Text, Modal, ModalInner } from 'components';
import { DuplicateOutlineIcon } from 'assets/svgs';

interface TLEData {
  line1: {
    satelliteNumber: number;
    classification: string;
    internationalDesignator: string;
    epochYear: number;
    epochDay: number;
    firstDerivative: number;
    secondDerivative: number;
    dragTerm: number;
    elementSetNumber: number;
    checksum: number;
  };
  line2: {
    satelliteNumber: number;
    inclination: number;
    rightAscensionOfAscendingNode: number;
    eccentricity: number;
    argumentOfPerigee: number;
    meanAnomaly: number;
    meanMotion: number;
    revolutionNumberAtEpoch: number;
    checksum: number;
  };
  raw: {
    line1: string;
    line2: string;
  };
}

function parseLine1(line1: string): TLEData['line1'] {
  if (line1.length < 69) {
    throw new Error('TLE Line 1 is too short (need >= 69 chars)');
  }

  const satelliteNumber = parseInt(line1.substring(2, 7), 10);
  const classification = line1.charAt(7);
  const internationalDesignator = line1.substring(9, 17).trim();
  const epochYear = parseInt(line1.substring(18, 20), 10);
  const epochDay = parseFloat(line1.substring(20, 32));
  const firstDerivative = parseFloat(line1.substring(33, 43));
  const secondDerivative = parseFloat(line1.substring(44, 52));
  const dragTerm = parseFloat(line1.substring(53, 61));
  const elementSetNumber = parseInt(line1.substring(64, 68), 10);
  const checksum = parseInt(line1.charAt(68), 10);

  return {
    satelliteNumber,
    classification,
    internationalDesignator,
    epochYear: epochYear < 57 ? 2000 + epochYear : 1900 + epochYear,
    epochDay,
    firstDerivative,
    secondDerivative,
    dragTerm,
    elementSetNumber,
    checksum,
  };
}

function parseLine2(line2: string): TLEData['line2'] {
  if (line2.length < 69) {
    throw new Error('TLE Line 2 is too short (need >= 69 chars)');
  }

  const satelliteNumber = parseInt(line2.substring(2, 7), 10);
  const inclination = parseFloat(line2.substring(8, 16));
  const rightAscensionOfAscendingNode = parseFloat(line2.substring(17, 25));
  const eccentricity = parseFloat('0.' + line2.substring(26, 33));
  const argumentOfPerigee = parseFloat(line2.substring(34, 42));
  const meanAnomaly = parseFloat(line2.substring(43, 51));
  const meanMotion = parseFloat(line2.substring(52, 63));
  const revolutionNumberAtEpoch = parseInt(line2.substring(63, 68), 10);
  const checksum = parseInt(line2.charAt(68), 10);

  return {
    satelliteNumber,
    inclination,
    rightAscensionOfAscendingNode,
    eccentricity,
    argumentOfPerigee,
    meanAnomaly,
    meanMotion,
    revolutionNumberAtEpoch,
    checksum,
  };
}

function validateChecksum(line: string): boolean {
  if (line.length < 69) return false;

  let sum = 0;
  for (let i = 0; i < 68; i++) {
    const char = line.charAt(i);
    if (char === '-') {
      sum += 1;
    } else if (char >= '0' && char <= '9') {
      sum += parseInt(char, 10);
    }
  }

  const checksum = parseInt(line.charAt(68), 10);
  return sum % 10 === checksum;
}

function parseTLE(line1: string, line2: string): TLEData {
  if (!line1.trim() || !line2.trim()) {
    throw new Error('TLE lines are empty');
  }

  if (!line1.startsWith('1 ') || !line2.startsWith('2 ')) {
    throw new Error("Invalid TLE line numbers (must start with '1 ' and '2 ')");
  }

  if (!validateChecksum(line1)) {
    throw new Error('Invalid checksum for TLE Line 1');
  }
  if (!validateChecksum(line2)) {
    throw new Error('Invalid checksum for TLE Line 2');
  }

  const parsedLine1 = parseLine1(line1);
  const parsedLine2 = parseLine2(line2);

  if (parsedLine1.satelliteNumber !== parsedLine2.satelliteNumber) {
    throw new Error('Satellite numbers do not match between lines');
  }

  return {
    line1: parsedLine1,
    line2: parsedLine2,
    raw: { line1, line2 },
  };
}

function epochToDate(epochYear: number, epochDay: number): Date {
  const startOfYear = new Date(epochYear, 0, 1);
  const millisecondsPerDay = 24 * 60 * 60 * 1000;
  const epochTime = startOfYear.getTime() + (epochDay - 1) * millisecondsPerDay;
  return new Date(epochTime);
}

function calculateOrbitalPeriod(meanMotion: number): number {
  if (meanMotion <= 0) return 0;
  return (24 * 60) / meanMotion;
}

function calculateAltitude(meanMotion: number): number {
  if (meanMotion <= 0) return 0;
  const GM = 3.986004418e14;
  const a = Math.pow(GM / Math.pow((meanMotion * 2 * Math.PI) / 86400, 2), 1 / 3);
  const RE = 6371000;
  return (a - RE) / 1000;
}

const TLEDataConverter = () => {
  const [tleData, setTleData] = useState('');
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [convertedData, setConvertedData] = useState<any>({
    satelliteNumber: '',
    classification: '',
    internationalDesignator: '',
    epochYear: '',
    epochDay: '',
    epochTime: '',
    inclination: '',
    raan: '',
    eccentricity: '',
    argumentOfPerigee: '',
    meanAnomaly: '',
    meanMotion: '',
    orbitalPeriod: '',
    meanAltitude: '',
    revolutionNumber: '',
    line1Checksum: '',
    line2Checksum: '',
    elementSetNumber: '',
    firstDerivative: '',
    secondDerivative: '',
    bstarDragTerm: ''
  });

  const handleConvert = () => {
    try {
      const lines = tleData.trim().split('\n').filter(line => line.trim());
      
      if (lines.length !== 2 && lines.length !== 3) {
        throw new Error('TLE must be 2-3 lines (Line 1, Line 2, and optional name)');
      }

      let line1Str = '';
      let line2Str = '';
      
      if (lines.length === 2) {
        line1Str = lines[0];
        line2Str = lines[1];
      } else {
        line1Str = lines[1];
        line2Str = lines[2];
      }

      const data = parseTLE(line1Str, line2Str);
      
      setConvertedData({
        satelliteNumber: data.line1.satelliteNumber.toString(),
        classification: data.line1.classification,
        internationalDesignator: data.line1.internationalDesignator,
        epochYear: data.line1.epochYear.toString(),
        epochDay: data.line1.epochDay.toFixed(8),
        epochTime: epochToDate(data.line1.epochYear, data.line1.epochDay).toISOString(),
        inclination: data.line2.inclination.toFixed(4),
        raan: data.line2.rightAscensionOfAscendingNode.toFixed(4),
        eccentricity: data.line2.eccentricity.toFixed(7),
        argumentOfPerigee: data.line2.argumentOfPerigee.toFixed(4),
        meanAnomaly: data.line2.meanAnomaly.toFixed(4),
        meanMotion: data.line2.meanMotion.toFixed(8),
        orbitalPeriod: calculateOrbitalPeriod(data.line2.meanMotion).toFixed(2),
        meanAltitude: calculateAltitude(data.line2.meanMotion).toFixed(1),
        revolutionNumber: data.line2.revolutionNumberAtEpoch.toString(),
        line1Checksum: `${data.line1.checksum} (verified)`,
        line2Checksum: `${data.line2.checksum} (verified)`,
        elementSetNumber: data.line1.elementSetNumber.toString(),
        firstDerivative: data.line1.firstDerivative.toExponential(5),
        secondDerivative: data.line1.secondDerivative.toExponential(5),
        bstarDragTerm: data.line1.dragTerm.toExponential(5),
      });
    } catch (err) {
      console.error('TLE parsing error:', err);
      setAlertMessage(err instanceof Error ? err.message : 'Error parsing TLE data');
    }
  };

  const handleClear = () => {
    setTleData('');
    setConvertedData({
      satelliteNumber: '',
      classification: '',
      internationalDesignator: '',
      epochYear: '',
      epochDay: '',
      epochTime: '',
      inclination: '',
      raan: '',
      eccentricity: '',
      argumentOfPerigee: '',
      meanAnomaly: '',
      meanMotion: '',
      orbitalPeriod: '',
      meanAltitude: '',
      revolutionNumber: '',
      line1Checksum: '',
      line2Checksum: '',
      elementSetNumber: '',
      firstDerivative: '',
      secondDerivative: '',
      bstarDragTerm: '',
    });
  };

  const handleCopy = async (value: string) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      console.log('Copied:', value);
    } catch (err) {
      console.error('Failed to copy:', err);
      setAlertMessage('Failed to copy to clipboard');
    }
  };

  return (
    <div className="space-y-8 px-4 py-6">
      {alertMessage && (
        <Modal isOpened={true}>
          <ModalInner title="Error" onClickClose={() => setAlertMessage(null)} className="md:w-[400px]">
            <div className="p-4 pt-0">
              <p className="text-body1 text-center" style={{ whiteSpace: 'pre-wrap' }}>
                {alertMessage}
              </p>
            </div>
            <div className="flex w-full flex-row items-center justify-end bg-bg-transparent_gray_light p-4">
              <Button className="h-9" text="OK" onClick={() => setAlertMessage(null)} size="large" />
            </div>
          </ModalInner>
        </Modal>
      )}
      <div className="bg-gray-50 rounded-lg p-4">
        <Text size="heading3" weight="medium" color="black" className="mb-4">
          TLE Data (Two-Line Element Set)
        </Text>
        
        <div className="space-y-4">
          <InputTextArea
            inputClassName="h-32 font-mono text-sm"
            placeholder={`1 25544U 98067A   08264.51782528 -.00002182  00000-0 -11606-4 0  2927
2 25544  51.6416 247.4627 0006703 130.5360 325.0288 15.72125391563537`}
            value={tleData}
            className="bg-white"
            setValue={setTleData}
          />
          <div className="flex gap-4">
            <Button
              type="blue"
              text="Convert TLE"
              size="large"
              onClick={handleConvert}
              className="flex-1 items-center justify-center"
            />
            <Button
              type="secondary"
              text="Clear"
              size="large"
              onClick={handleClear}
              className="flex-1 items-center justify-center"
            />
          </div>
        </div>
      </div>

      {/* Converted TLE Data Results */}
      <div className="bg-gray-100 rounded-lg p-6 space-y-4">
        <Text size="heading3" weight="medium" color="black" className="mb-3">Identification & Epoch Information</Text>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
            <div className="flex-1 mr-2">
              <Text size="body2" weight="medium" color="secondary" className="mb-1">Satellite Number</Text>
              <Text size="body1" weight="regular" color="black">{convertedData.satelliteNumber || '-'}</Text>
            </div>
            <button
              onClick={() => handleCopy(convertedData.satelliteNumber)}
              className="p-1 hover:bg-gray-200 rounded disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
              disabled={!convertedData.satelliteNumber}
            >
              <DuplicateOutlineIcon />
            </button>
          </div>
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
            <div className="flex-1 mr-2">
              <Text size="body2" weight="medium" color="secondary" className="mb-1">Classification</Text>
              <Text size="body1" weight="regular" color="black">{convertedData.classification || '-'}</Text>
            </div>
            <button
              onClick={() => handleCopy(convertedData.classification)}
              className="p-1 hover:bg-gray-200 rounded disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
              disabled={!convertedData.classification}
            >
              <DuplicateOutlineIcon />
            </button>
          </div>
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
            <div className="flex-1 mr-2">
              <Text size="body2" weight="medium" color="secondary" className="mb-1">International Designator</Text>
              <Text size="body1" weight="regular" color="black">{convertedData.internationalDesignator || '-'}</Text>
            </div>
            <button
              onClick={() => handleCopy(convertedData.internationalDesignator)}
              className="p-1 hover:bg-gray-200 rounded disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
              disabled={!convertedData.internationalDesignator}
            >
              <DuplicateOutlineIcon />
            </button>
          </div>
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
            <div className="flex-1 mr-2">
              <Text size="body2" weight="medium" color="secondary" className="mb-1">Epoch Year</Text>
              <Text size="body1" weight="regular" color="black">{convertedData.epochYear || '-'}</Text>
            </div>
            <button
              onClick={() => handleCopy(convertedData.epochYear)}
              className="p-1 hover:bg-gray-200 rounded disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
              disabled={!convertedData.epochYear}
            >
              <DuplicateOutlineIcon />
            </button>
          </div>
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
            <div className="flex-1 mr-2">
              <Text size="body2" weight="medium" color="secondary" className="mb-1">Epoch Day</Text>
              <Text size="body1" weight="regular" color="black">{convertedData.epochDay || '-'}</Text>
            </div>
            <button
              onClick={() => handleCopy(convertedData.epochDay)}
              className="p-1 hover:bg-gray-200 rounded disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
              disabled={!convertedData.epochDay}
            >
              <DuplicateOutlineIcon />
            </button>
          </div>
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
            <div className="flex-1 mr-2">
              <Text size="body2" weight="medium" color="secondary" className="mb-1">Epoch Time (UTC)</Text>
              <Text size="body1" weight="regular" color="black">{convertedData.epochTime || '-'}</Text>
            </div>
            <button
              onClick={() => handleCopy(convertedData.epochTime)}
              className="p-1 hover:bg-gray-200 rounded disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
              disabled={!convertedData.epochTime}
            >
              <DuplicateOutlineIcon />
            </button>
          </div>
        </div>

        <div>
          <Text size="heading3" weight="medium" color="black" className="mb-3">Orbital Parameters</Text>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
              <div className="flex-1 mr-2">
                <Text size="body2" weight="medium" color="secondary" className="mb-1">Inclination (i) [deg]</Text>
                <Text size="body1" weight="regular" color="black">{convertedData.inclination || '-'}</Text>
              </div>
              <button
                onClick={() => handleCopy(convertedData.inclination)}
                className="p-1 hover:bg-gray-200 rounded disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                disabled={!convertedData.inclination}
              >
                <DuplicateOutlineIcon />
              </button>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
              <div className="flex-1 mr-2">
                <Text size="body2" weight="medium" color="secondary" className="mb-1">RAAN (Ω) [deg]</Text>
                <Text size="body1" weight="regular" color="black">{convertedData.raan || '-'}</Text>
              </div>
              <button
                onClick={() => handleCopy(convertedData.raan)}
                className="p-1 hover:bg-gray-200 rounded disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                disabled={!convertedData.raan}
              >
                <DuplicateOutlineIcon />
              </button>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
              <div className="flex-1 mr-2">
                <Text size="body2" weight="medium" color="secondary" className="mb-1">Eccentricity (e)</Text>
                <Text size="body1" weight="regular" color="black">{convertedData.eccentricity || '-'}</Text>
              </div>
              <button
                onClick={() => handleCopy(convertedData.eccentricity)}
                className="p-1 hover:bg-gray-200 rounded disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                disabled={!convertedData.eccentricity}
              >
                <DuplicateOutlineIcon />
              </button>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
              <div className="flex-1 mr-2">
                <Text size="body2" weight="medium" color="secondary" className="mb-1">Argument of Perigee (ω) [deg]</Text>
                <Text size="body1" weight="regular" color="black">{convertedData.argumentOfPerigee || '-'}</Text>
              </div>
              <button
                onClick={() => handleCopy(convertedData.argumentOfPerigee)}
                className="p-1 hover:bg-gray-200 rounded disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                disabled={!convertedData.argumentOfPerigee}
              >
                <DuplicateOutlineIcon />
              </button>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
              <div className="flex-1 mr-2">
                <Text size="body2" weight="medium" color="secondary" className="mb-1">Mean Anomaly (M) [deg]</Text>
                <Text size="body1" weight="regular" color="black">{convertedData.meanAnomaly || '-'}</Text>
              </div>
              <button
                onClick={() => handleCopy(convertedData.meanAnomaly)}
                className="p-1 hover:bg-gray-200 rounded disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                disabled={!convertedData.meanAnomaly}
              >
                <DuplicateOutlineIcon />
              </button>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
              <div className="flex-1 mr-2">
                <Text size="body2" weight="medium" color="secondary" className="mb-1">Mean Motion (n) [rev/day]</Text>
                <Text size="body1" weight="regular" color="black">{convertedData.meanMotion || '-'}</Text>
              </div>
              <button
                onClick={() => handleCopy(convertedData.meanMotion)}
                className="p-1 hover:bg-gray-200 rounded disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                disabled={!convertedData.meanMotion}
              >
                <DuplicateOutlineIcon />
              </button>
            </div>
          </div>
        </div>

        <div>
          <Text size="heading3" weight="medium" color="black" className="mb-3">Derived and Additional Parameters</Text>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
              <div className="flex-1 mr-2">
                <Text size="body2" weight="medium" color="secondary" className="mb-1">Orbital Period [min]</Text>
                <Text size="body1" weight="regular" color="black">{convertedData.orbitalPeriod || '-'}</Text>
              </div>
              <button
                onClick={() => handleCopy(convertedData.orbitalPeriod)}
                className="p-1 hover:bg-gray-200 rounded disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                disabled={!convertedData.orbitalPeriod}
              >
                <DuplicateOutlineIcon />
              </button>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
              <div className="flex-1 mr-2">
                <Text size="body2" weight="medium" color="secondary" className="mb-1">Mean Altitude [km]</Text>
                <Text size="body1" weight="regular" color="black">{convertedData.meanAltitude || '-'}</Text>
              </div>
              <button
                onClick={() => handleCopy(convertedData.meanAltitude)}
                className="p-1 hover:bg-gray-200 rounded disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                disabled={!convertedData.meanAltitude}
              >
                <DuplicateOutlineIcon />
              </button>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
              <div className="flex-1 mr-2">
                <Text size="body2" weight="medium" color="secondary" className="mb-1">Revolution Number at Epoch</Text>
                <Text size="body1" weight="regular" color="black">{convertedData.revolutionNumber || '-'}</Text>
              </div>
              <button
                onClick={() => handleCopy(convertedData.revolutionNumber)}
                className="p-1 hover:bg-gray-200 rounded disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                disabled={!convertedData.revolutionNumber}
              >
                <DuplicateOutlineIcon />
              </button>
            </div>
          </div>
        </div>

        <div>
          <Text size="heading3" weight="medium" color="black" className="mb-3">Checksums and Derivatives</Text>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
              <div className="flex-1 mr-2">
                <Text size="body2" weight="medium" color="secondary" className="mb-1">Line 1 Checksum</Text>
                <Text size="body1" weight="regular" color="black">{convertedData.line1Checksum || '-'}</Text>
              </div>
              <button
                onClick={() => handleCopy(convertedData.line1Checksum)}
                className="p-1 hover:bg-gray-200 rounded disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                disabled={!convertedData.line1Checksum}
              >
                <DuplicateOutlineIcon />
              </button>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
              <div className="flex-1 mr-2">
                <Text size="body2" weight="medium" color="secondary" className="mb-1">Line 2 Checksum</Text>
                <Text size="body1" weight="regular" color="black">{convertedData.line2Checksum || '-'}</Text>
              </div>
              <button
                onClick={() => handleCopy(convertedData.line2Checksum)}
                className="p-1 hover:bg-gray-200 rounded disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                disabled={!convertedData.line2Checksum}
              >
                <DuplicateOutlineIcon />
              </button>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
              <div className="flex-1 mr-2">
                <Text size="body2" weight="medium" color="secondary" className="mb-1">Element Set Number</Text>
                <Text size="body1" weight="regular" color="black">{convertedData.elementSetNumber || '-'}</Text>
              </div>
              <button
                onClick={() => handleCopy(convertedData.elementSetNumber)}
                className="p-1 hover:bg-gray-200 rounded disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                disabled={!convertedData.elementSetNumber}
              >
                <DuplicateOutlineIcon />
              </button>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
              <div className="flex-1 mr-2">
                <Text size="body2" weight="medium" color="secondary" className="mb-1">d(n)/dt [rev/day²]</Text>
                <Text size="body1" weight="regular" color="black">{convertedData.firstDerivative || '-'}</Text>
              </div>
              <button
                onClick={() => handleCopy(convertedData.firstDerivative)}
                className="p-1 hover:bg-gray-200 rounded disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                disabled={!convertedData.firstDerivative}
              >
                <DuplicateOutlineIcon />
              </button>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
              <div className="flex-1 mr-2">
                <Text size="body2" weight="medium" color="secondary" className="mb-1">d²(n)/dt² [rev/day³]</Text>
                <Text size="body1" weight="regular" color="black">{convertedData.secondDerivative || '-'}</Text>
              </div>
              <button
                onClick={() => handleCopy(convertedData.secondDerivative)}
                className="p-1 hover:bg-gray-200 rounded disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                disabled={!convertedData.secondDerivative}
              >
                <DuplicateOutlineIcon />
              </button>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
              <div className="flex-1 mr-2">
                <Text size="body2" weight="medium" color="secondary" className="mb-1">BSTAR Drag Term [1/er]</Text>
                <Text size="body1" weight="regular" color="black">{convertedData.bstarDragTerm || '-'}</Text>
              </div>
              <button
                onClick={() => handleCopy(convertedData.bstarDragTerm)}
                className="p-1 hover:bg-gray-200 rounded disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                disabled={!convertedData.bstarDragTerm}
              >
                <DuplicateOutlineIcon />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TLEDataConverter;
